// supervisor-outcome-router-g0-delegation-claim-path.ts — phase 06d R1
// (dogfood lần 3, F-3; spec V2 §3 E17). Router branch run BEFORE the
// needs_decision path, same split pattern as the other `*-path.ts` files so
// supervisor-outcome-router.ts stays under the 200-line budget. Predicates
// (claim keys, exemptions B6, acceptance N6) live in
// worker-decisions-g0-delegation-claim-guard.ts; this file adds the B12
// exemption source (this ticket's delegated answers) and the two outcomes:
// escalate (the running ticket's acceptance delegates — B5) or retry as
// `task_failed` with the R1 reason (B4).
import { tagStopDecision } from "./escalation-blocked-by.js";
import type { MissionStore } from "./mission-store.js";
import {
  type RetryConfig,
  type RetryDecision,
  type RetryState,
  recordAttemptOutcome,
  redactEvidence,
} from "./retry-policy.js";
import { readDecidedQuestionsForTicket } from "./run-command-prompt-decided-questions-section.js";
import type { AttemptCycleResult } from "./supervisor-attempt-cycle.js";
import { escalate, journalRejectionReceipt } from "./supervisor-outcome-router-done-path.js";
import { workerDeclaredOutcome } from "./supervisor-outcome-router-worker-outcome.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";
import {
  ACCEPTANCE_CLIP_CHARS,
  acceptanceDelegatingToWorker,
  findG0DelegationClaim,
} from "./worker-decisions-g0-delegation-claim-guard.js";

const CLAIM_ID_MAX_CHARS = 64;

/** B12: `answered_by` (+ question id) of THIS ticket's delegated answers — exact match only. */
function delegatedAnswerQuotes(controlRoot: string, ticketId: string): Set<string> {
  const quotes = new Set<string>();
  for (const { question, answer } of readDecidedQuestionsForTicket(controlRoot, ticketId)) {
    if (!answer.answered_by.startsWith("delegated:")) continue;
    quotes.add(answer.answered_by);
    quotes.add(question.id);
  }
  return quotes;
}

export interface G0ClaimRouteInput {
  cycle: AttemptCycleResult;
  controlRoot: string;
  repoDir: string;
  ticketId: string;
  store: MissionStore;
  retryState: RetryState;
  retryConfig: RetryConfig;
  now: Date;
}

/** Running ticket's delegating acceptance; unreadable tickets ⇒ `undefined` (plain retry). */
async function runningTicketDelegation(
  repoDir: string,
  ticketId: string,
): Promise<string | undefined> {
  try {
    const running = (await loadTicketsFromRepo(repoDir)).find((t) => t.id === ticketId);
    return acceptanceDelegatingToWorker(running?.acceptance);
  } catch {
    return undefined;
  }
}

/**
 * R1 route, called BEFORE the needs_decision path. `undefined` ⇒ out of scope
 * or no claim: the old path runs untouched (contract_error/recovering verdicts
 * and failed/blocked outcomes keep their category — N2/N3 vòng 2).
 */
export async function routeG0DelegationClaim(
  input: G0ClaimRouteInput,
  secretValues: string[],
): Promise<{ kind: "retry_decision"; decision: RetryDecision } | undefined> {
  const { cycle } = input;
  if (cycle.kind !== "verified") return undefined;
  if (cycle.verdict.kind !== "done" && cycle.verdict.kind !== "rejected") return undefined;
  const outcome = workerDeclaredOutcome(cycle);
  if (outcome !== "done" && outcome !== "needs_decision") return undefined;
  const claim = findG0DelegationClaim(
    cycle.sessionResult.result,
    delegatedAnswerQuotes(input.controlRoot, input.ticketId),
  );
  if (claim === undefined) return undefined;
  // Mask the whole id (patterns + known values), THEN cut — a cut first would
  // leave a secret's prefix; the escalate path has no redactor of its own.
  const id = redactEvidence(claim.id, secretValues).slice(0, CLAIM_ID_MAX_CHARS);
  const reason =
    `worker_applied_g0_delegation_rejected: ${id} — chỉ controller áp G0; gặp quyết định mở ⇒ ` +
    "trả needs_decision (options + recommended_option_id), không tự chọn";
  // B4 merge of the verifier's reasons on BOTH branches (review L4).
  const verifierReasons = cycle.verdict.kind === "rejected" ? cycle.verdict.reasons : [];
  const delegating = await runningTicketDelegation(input.repoDir, input.ticketId);
  if (delegating !== undefined) {
    await journalRejectionReceipt(input.store, "ticket_acceptance_delegates_open_decision");
    const escalateReason =
      `ticket_acceptance_delegates_open_decision: acceptance của ${input.ticketId} ` +
      `${JSON.stringify(redactEvidence(delegating, secretValues).slice(0, ACCEPTANCE_CLIP_CHARS))} ` +
      `giao worker tự quyết — retry chỉ lặp lại lời giao đó, cần người sửa ticket; ${reason}`;
    return escalate(
      input.retryState,
      [escalateReason, ...verifierReasons.map((r) => redactEvidence(r, secretValues))].join("; "),
      undefined,
      "g0_claim",
    );
  }
  const evidence = [reason, ...verifierReasons].join("; ");
  const decision = recordAttemptOutcome(
    input.retryState,
    { category: "task_failed", evidence, secretValues },
    input.retryConfig,
    input.now,
  );
  return { kind: "retry_decision", decision: tagStopDecision(decision, "g0_claim") };
}
